import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useMarquerLues,
  useNotifications,
  type CanalNotification,
  type LigneNotification,
} from '../../lib/useNotifications';
import { adresseQuestion } from '../../lib/useLecteur';

interface Props {
  ouvert: boolean;
  onFermer: () => void;
}

function formaterDate(valeur: string): string {
  try {
    const d = new Date(valeur);
    const maintenant = new Date();
    const diffMs = maintenant.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const diffH = Math.floor(diffMin / 60);

    if (diffMin < 1) return 'À l’instant';
    if (diffMin < 60) return `Il y a ${diffMin} min`;
    if (diffH < 24) return `Il y a ${diffH} h`;

    return d.toLocaleDateString('fr-FR', {
      timeZone: 'Indian/Reunion',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return valeur;
  }
}

/** Icône du premier canal de l'envoi ; les libellés des pastilles viennent de l'API. */
const ICONES_CANAL: Record<CanalNotification, JSX.Element> = {
  IN_APP: (
    <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  ),
  EMAIL: (
    <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  ),
  SMS: (
    <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  ),
  PUSH: (
    <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  ),
};

export function NotificationPopup({ ouvert, onFermer }: Props): JSX.Element | null {
  const conteneurRef = useRef<HTMLDivElement>(null);
  const [onglet, setOnglet] = useState<'toutes' | 'nonLues'>('toutes');

  const { data, isLoading } = useNotifications({});
  const marquerLues = useMarquerLues();

  const notifications = useMemo(() => data?.notifications ?? [], [data]);
  const compteurs = data?.compteurs;
  const nonLuesCount = compteurs?.nonLues ?? 0;

  const notificationsFiltrees = useMemo(() => {
    if (onglet === 'nonLues') {
      return notifications.filter((n) => !n.lu);
    }
    return notifications;
  }, [notifications, onglet]);

  // Fermeture lors d'un clic extérieur ou appui sur Échap
  useEffect(() => {
    if (!ouvert) return;

    function handlePointerDown(e: MouseEvent) {
      if (conteneurRef.current && !conteneurRef.current.contains(e.target as Node)) {
        onFermer();
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onFermer();
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [ouvert, onFermer]);

  if (!ouvert) return null;

  function marquerUne(e: React.MouseEvent, notification: LigneNotification) {
    e.stopPropagation();
    if (!notification.lu) {
      marquerLues.mutate([notification.id]);
    }
  }

  function marquerToutes() {
    if (nonLuesCount > 0) {
      marquerLues.mutate(undefined);
    }
  }

  return (
    // Sous `sm`, la cloche n'est pas au bord de l'écran : la fenêtre se cale
    // sur la largeur de l'écran plutôt que sur le bouton, qui la décalait à gauche.
    <div
      ref={conteneurRef}
      className="fixed inset-x-3 top-[4.5rem] z-50 mx-auto max-w-sm sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mx-0 sm:mt-2 sm:w-96 rounded-2xl border border-gray-200/80 bg-white shadow-2xl overflow-hidden transition-all duration-200"
      style={{
        boxShadow: '0 20px 40px -12px rgba(11, 31, 58, 0.25)',
      }}
      role="dialog"
      aria-label="Centre de notifications"
    >
      {/* En-tête du pop-up */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 bg-gradient-to-r from-gray-50/80 to-white">
        <div className="flex items-center gap-2">
          <span className="font-display text-sm font-bold text-codialis-navy">Notifications</span>
          {nonLuesCount > 0 ? (
            <span className="inline-flex items-center rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">
              {nonLuesCount}
            </span>
          ) : (
            <span className="badge badge-mint text-[10px]">À jour</span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {nonLuesCount > 0 && (
            <button
              type="button"
              onClick={marquerToutes}
              disabled={marquerLues.isPending}
              className="text-[11px] font-semibold text-codialis-sky hover:text-codialis-navy hover:underline transition disabled:opacity-50"
            >
              Tout lire
            </button>
          )}
          <button
            type="button"
            onClick={onFermer}
            aria-label="Fermer"
            className="rounded-lg p-1 text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy"
          >
            <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Onglets rapides */}
      <div className="flex border-b border-gray-100 px-3 bg-white text-xs">
        <button
          type="button"
          onClick={() => setOnglet('toutes')}
          aria-pressed={onglet === 'toutes'}
          className={`px-3 py-2 font-medium border-b-2 transition ${
            onglet === 'toutes'
              ? 'border-codialis-mint text-codialis-navy font-semibold'
              : 'border-transparent text-codialis-gray hover:text-codialis-navy'
          }`}
        >
          Toutes ({compteurs?.total ?? notifications.length})
        </button>
        <button
          type="button"
          onClick={() => setOnglet('nonLues')}
          aria-pressed={onglet === 'nonLues'}
          className={`px-3 py-2 font-medium border-b-2 transition ${
            onglet === 'nonLues'
              ? 'border-codialis-mint text-codialis-navy font-semibold'
              : 'border-transparent text-codialis-gray hover:text-codialis-navy'
          }`}
        >
          Non lues ({nonLuesCount})
        </button>
      </div>

      {/* Liste déroulante des notifications */}
      <div className="scrollbar-thin max-h-80 overflow-y-auto divide-y divide-gray-100 bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-xs text-codialis-gray">
            <svg aria-hidden="true" className="h-5 w-5 animate-spin text-codialis-mint" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="ml-2">Chargement…</span>
          </div>
        ) : notificationsFiltrees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-codialis-sand text-codialis-mint mb-2">
              <svg aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="text-xs font-semibold text-codialis-navy">
              {onglet === 'nonLues' ? 'Aucune notification non lue' : 'Aucune notification reçue'}
            </div>
            <p className="mt-1 text-[11px] text-codialis-gray">
              Vous êtes parfaitement à jour dans vos suivis et alertes.
            </p>
          </div>
        ) : (
          notificationsFiltrees.map((item) => {
            const premierCanal = item.canaux[0]?.canal ?? 'IN_APP';
            return (
              <div
                key={item.id}
                onClick={(e) => marquerUne(e, item)}
                className={`group relative flex items-start gap-3 p-3 text-left transition hover:bg-gray-50 cursor-pointer ${
                  !item.lu ? 'bg-amber-50/40' : 'bg-white'
                }`}
              >
                {/* Indicateur de lecture */}
                <div className="pt-1 flex flex-col items-center gap-1.5 shrink-0">
                  <div
                    className={`h-2 w-2 rounded-full transition ${
                      !item.lu ? 'bg-red-500 ring-2 ring-red-200' : 'bg-transparent'
                    }`}
                  />
                  <div
                    className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                      premierCanal === 'EMAIL'
                        ? 'bg-amber-100 text-amber-800'
                        : premierCanal === 'SMS'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {ICONES_CANAL[premierCanal]}
                  </div>
                </div>

                {/* Contenu textuel */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-semibold text-codialis-navy truncate">
                      {item.titre}
                    </span>
                    <span className="shrink-0 text-[10px] text-codialis-gray">
                      {formaterDate(item.envoyeeLe)}
                    </span>
                  </div>

                  <p className="mt-0.5 text-[11px] text-codialis-gray line-clamp-2 leading-relaxed">
                    {item.corps}
                  </p>

                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
                    {item.canaux.map((canal) => (
                      <span key={canal.canal} className={`badge badge-${canal.ton}`}>
                        {canal.libelle}
                      </span>
                    ))}

                    {item.apprenant && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-codialis-navy">
                        {item.apprenant}
                      </span>
                    )}

                    {/* Une question de la discussion d'un module s'ouvre dans le lecteur (TF-448). */}
                    {item.discussion && (
                      <Link
                        to={adresseQuestion(item.discussion.leconId, item.discussion.questionId)}
                        onClick={(e) => {
                          marquerUne(e, item);
                          onFermer();
                        }}
                        className="font-semibold text-codialis-sky hover:underline"
                      >
                        Voir la question
                      </Link>
                    )}

                    {!item.lu && (
                      <button
                        type="button"
                        onClick={(e) => marquerUne(e, item)}
                        className="ml-auto opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 text-[10px] font-semibold text-codialis-sky hover:underline transition"
                      >
                        Marquer comme lu
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pied de popup : Lien vers la page dédiée */}
      <div className="border-t border-gray-100 bg-gray-50/80 px-4 py-2.5 text-center">
        <Link
          to="/notifications"
          onClick={onFermer}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-codialis-navy hover:text-codialis-sky transition"
        >
          <span>Voir tout l’historique des notifications</span>
          <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
